import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Box,
  TextField,
  Typography,
  IconButton,
  Grid2,
} from "@mui/material";
import useValidation from "../../hooks/useValidation";
import Background from "../../components/Background";
import { styled } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import {
  clearVerifyOtp,
  forgotPassword,
  verifyForgotPwdCode,
} from "../../redux/authSlice";
import Path from "../../router/LocalRoutes";
import Topnav from "../../components/Topnav";
import Copyrighttext from "../../components/Copyrighttext";
import ArrowBackIcon from "../../assets/images/Arrow Left.svg"; // Import back arrow icon
import "./EnterSecurityCode.css";
import "./Signin.css";
import { baseFetch } from "../../helpers/apiClient";

// Custom styling for the OTP input fields
const OtpInput = styled(TextField)(({ theme }) => ({
  width: "60px",
  height: "60px",
  textAlign: "center",
  margin: theme.spacing(1),
  "& .MuiInputBase-input": {
    textAlign: "center",
    fontSize: "24px",
    padding: "16px",
  },

  "& .MuiOutlinedInput-root": {
    borderRadius: "70px",
    backgroundColor: "#f1f5f9",
    width: "60px !important",
    height: "50px !important",
  },
  "& input[type=number]": {
    MozAppearance: "textfield", // Firefox
  },
  "& input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button":
    {
      WebkitAppearance: "none", // Chrome, Safari, Edge
      margin: 0,
    },
  [theme.breakpoints.down("sm")]: {
    width: "60px !important", // Adjust size for small screens
    height: "50px !important",
    "& .MuiInputBase-input": {
      fontSize: "18px",
      padding: "14px",
    },
  },
}));

const EnterSecurityCode = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  const [time, setTime] = useState(120);
  const register = location?.state?.register || false;
  const token = register
    ? localStorage.getItem("verifyEmailToken")
    : location?.state?.res?.result?.resetPasswordToken;
  const [refreshToken, setRefreshToken] = useState(token);
  const [otp, setOtp] = useState(new Array(4).fill(""));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (auth.verifyStatus === "failed") {
      dispatch(clearVerifyOtp());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return;

    let newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    // Automatically move to the next input if a digit is entered
    if (element.value && index < 3) {
      inputRefs.current[index + 1].focus();
    }

    if (auth.verifyStatus === "failed") {
      dispatch(clearVerifyOtp());
    }
  };

  // Handle pasting a full OTP code (e.g. "1234") by distributing
  // each digit across the individual input fields
  const handlePaste = (e) => {
    e.preventDefault();

    // Strip non-digit characters and limit to 4 digits
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);
    if (!pastedData) return;

    // Distribute each digit into its respective input slot
    const newOtp = [...otp];
    for (let i = 0; i < 4; i++) {
      newOtp[i] = pastedData[i] || "";
    }
    setOtp(newOtp);

    // Focus the last filled input (or the 4th if all filled)
    const focusIndex = Math.min(pastedData.length, 3);
    inputRefs.current[focusIndex]?.focus();

    if (auth.verifyStatus === "failed") {
      dispatch(clearVerifyOtp());
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && index > 0 && !otp[index]) {
      inputRefs.current[index - 1].focus();
    }
  };

  const { errors, validateInputs } = useValidation({
    otp: otp,
  });

  console.log(errors);

  useEffect(() => {
    if (time > 0) {
      const timer = setInterval(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);

      // Clean up the interval when the component is unmounted or when time changes
      return () => clearInterval(timer);
    }
  }, [time]);

  // Format the time to MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const handleVerifyOtp = async () => {
    if (!validateInputs()) {
      return;
    }
    const payload = {
      otp: otp.join(""),
      token: refreshToken,
    };
    dispatch(verifyForgotPwdCode(payload))
      .then((response) => {
        if (response?.payload?.status) {
          if (register) {
            navigate("/onboarding");
          } else {
            navigate(Path.NewPassword, {
              state: { token: refreshToken, prevData: location.state },
            });
          }
        }
      })
      .catch((error) => {
        console.log("err", error);
      });
  };

  const handleResendCode = () => {
    if (location.state?.register) {
      const payload = {
        email: location?.state?.email,
      };
      baseFetch({
        url: "/v1/auth/resend-otp",
        method: "POST",
        body: payload,
      })
        .then((response) => {
          if (response.status) {
            setOtp(new Array(4).fill(""));
            localStorage.setItem(
              "verifyEmailToken",
              response?.data?.verifyEmailToken,
            );
            setRefreshToken(response?.data?.verifyEmailToken);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      const payload = {
        email: location?.state?.email,
      };
      dispatch(forgotPassword(payload))
        .then((response) => {
          if (response?.payload?.status) {
            setOtp(new Array(4).fill(""));
            setRefreshToken(
              response?.payload?.data?.result?.resetPasswordToken,
            );
            setTime(90);
          }
        })
        .catch((error) => {
          console.log("err", error);
        });
    }
  };
  const disable = auth.verifyStatus === "loading" || otp.join("")?.length < 4;

  return (
    <Background>
      <Topnav />
      {!register && (
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            display: { xs: "flex", lg: "none" }, // Show on xs, sm, md, and hide on lg and up
            alignItems: "center !important",
            position: "relative",
            bgcolor: "#2A4876",
            color: "white",
            borderRadius: "20px", // Make it rounded
            padding: "8px 16px !important", // Adjust padding to get the pill shape
            "&:hover": { bgcolor: "#1F3553" },
            marginX: "20px",
            marginTop: "10px",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", marginRight: "8px" }}
          >
            <img src={ArrowBackIcon} alt="Back" />
          </Box>
          <Typography
            sx={{ fontWeight: "bold", fontSize: "14px", fontFamily: "PTSans" }}
          >
            Back
          </Typography>
        </IconButton>
      )}
      {/* Back Button in large devices */}
      {!register && (
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            display: { xs: "none", sm: "none", md: "none", lg: "flex" }, // Hide on xs, sm, md, and show on lg and up
            alignItems: "center !important",
            position: "absolute",
            left: "15vw",
            bgcolor: "#2A4876",
            color: "white",
            borderRadius: "20px", // Make it rounded
            padding: "8px 16px !important", // Adjust padding to get the pill shape
            "&:hover": { bgcolor: "#1F3553" },
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", marginRight: "8px" }}
          >
            <img src={ArrowBackIcon} alt="Back" />
          </Box>
          <Typography
            sx={{ fontWeight: "bold", fontSize: "14px", fontFamily: "PTSans" }}
          >
            Back
          </Typography>
        </IconButton>
      )}
      <div className="selection-box">
        <Typography
          className="text-primary text-xxl"
          variant="h5"
          sx={{ fontWeight: "700" }}
        >
          Enter Security Code
        </Typography>
        <Typography variant="body1" className="body">
          Enter the security code we sent to <br /> {location?.state?.email}
        </Typography>

        {/* OTP Inputs */}
        <Grid2
          container
          justifyContent="center"
          alignItems="center"
          sx={{
            flexDirection: "row", // Ensure inputs are in a row
            marginTop: "20px",
          }}
        >
          {otp.map((data, index) => (
            <OtpInput
              key={index}
              type="number"
              placeholder="-"
              slotProps={{
                htmlInput: {
                  maxLength: 1, // Restrict to 1 digit per input
                  style: {
                    fontSize: "14px", // Reduce the placeholder text size
                    color: "#2A4876",
                  },
                },
              }}
              value={data}
              onPaste={handlePaste}
              onChange={(e) => handleChange(e.target, index)}
              // onKeyDown={(e) => handleKeyDown(e, index)}
              onKeyDown={(e) => {
                if (e.target.value.length >= 1 && e.key !== "Backspace") {
                  e.preventDefault();
                }
                handleKeyDown(e, index);
              }}
              inputRef={(el) => (inputRefs.current[index] = el)}
              sx={{
                "& input::placeholder": {
                  fontSize: "14px", // Adjust placeholder text size here as well
                  color: "#2A4876",
                },
              }}
            />
          ))}
        </Grid2>

        {/* Timer and Resend Code */}
        <Grid2
          container
          justifyContent="space-between"
          sx={{ alignItems: "center" }}
        >
          <Typography variant="body2" color="#7BB9D5">
            {formatTime(time)}
          </Typography>
          <Button
            sx={{
              fontSize: "12px !important",
              borderRadius: "100px",
              height: "4px",
              fontWeight: "500",
              textTransform: "none",
              color: time > 0 ? "grey" : "black", // Change color when time > 0 (inactive)
            }}
            onClick={handleResendCode}
          >
            Resend Code
          </Button>
        </Grid2>

        {auth.verifyStatus === "failed" && (
          <Typography sx={{ color: "red", marginBottom: "15px" }}>
            {auth.verifyError}
          </Typography>
        )}

        {/* Verify Button */}
        <Button
          sx={{ marginTop: "15px" }}
          variant="contained"
          color="primary"
          fullWidth
          className="verify-button"
          style={{
            backgroundColor: !disable ? "#2A4876" : "#AAB6C8", // Change color based on input values
            cursor: !disable ? "pointer" : "not-allowed", // Disable hover effect when fields are not filled
            color: "#fff",
          }}
          onClick={handleVerifyOtp}
          disabled={!disable ? false : true}
        >
          Verify
        </Button>
      </div>
      <Box mt={2} sx={{ display: { xs: "block", sm: "none" } }}>
        <Copyrighttext />
      </Box>
      <Box
        sx={{
          display: { xs: "none", sm: "block" },
        }}
      >
        <Copyrighttext />
      </Box>
    </Background>
  );
};

export default EnterSecurityCode;
